/**
 * ContractorsPage — `/for-contractors`
 *
 * Built specifically for the irrigation-pro audience surfaced by Tiberias
 * Garrett's May 10 FB comment ("I do have some commercial that may take
 * interest"). The page is intentionally NOT homeowner-facing — it leans
 * hard into the technical/B2B side: stacks on top of MP Rotators +
 * Hydrawise + Rachio, doesn't compete with distribution work, comp first
 * season for verified pros.
 *
 * Voice rules: peer-to-peer, no hype. Match the same tone as the FB reply.
 * Acknowledge what existing pro stacks already do well, position TurfPulse
 * as the soil-truth override layer on top, not a replacement.
 *
 * Backend: POST /api/contractor-pilot (public, IP rate-limited).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Briefcase, Wrench, Gauge, ShieldCheck, ArrowRight,
  CheckCircle2, XCircle, MessageCircle,
} from 'lucide-react';
import { API_URL } from '../config/api';

const URL = 'https://getturfpulse.com/for-contractors';

const STACK_OPTIONS = ['Rachio', 'Hydrawise', 'Rain Bird IQ', 'Toro Sentinel', 'OpenSprinkler', 'Weathermatic', 'Calsense', 'Other'];

export default function ContractorsPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-amber-50/40 dark:from-zinc-950 dark:via-zinc-950 dark:to-amber-950/10"
      data-testid="contractors-page"
    >
      <SEO
        title="TurfPulse for Irrigation Contractors — Soil-Truth Layer on Top of Your Existing Stack"
        description="A soil-aware override layer that stacks on top of MP Rotators + Hydrawise + Rachio + Rain Bird IQ. Built for commercial properties with water reporting requirements. First season comp'd for verified pros."
        canonical={URL}
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'For Contractors', path: '/for-contractors' },
        ]}
      />

      <Hero />
      <ValueProps />
      <Honesty />
      <PilotForm />
      <Faq />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
const Hero = () => (
  <section
    className="border-b border-border bg-zinc-900 text-zinc-50 dark:bg-zinc-950"
    data-testid="contractors-hero"
  >
    <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
      <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-300">
        <Briefcase className="w-3.5 h-3.5" /> For Irrigation Pros
      </div>
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
        Stack soil-truth on top of<br />
        <span className="text-amber-300">the gear you already trust.</span>
      </h1>
      <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
        TurfPulse is not a Hydrawise replacement, not a controller, and not
        trying to compete with your MP Rotator install work. It's an automated
        soil-aware decision layer that sits on top of any controller with an
        API — and tells it whether the schedule actually needs to fire today.
      </p>
      <p className="mt-3 text-sm text-zinc-400 max-w-2xl leading-relaxed">
        Built for commercial properties with water reporting requirements,
        drought-restricted regions, and the residential clients who actually
        notice the bill. Honest about everything else.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <a href="#contractor-pilot-form">
          <Button
            size="lg"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold"
            data-testid="contractors-hero-cta"
          >
            Apply for the first-season pilot <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
        </a>
        <Link to="/ecowitt-to-rachio">
          <Button
            size="lg"
            variant="outline"
            className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 hover:text-zinc-50"
            data-testid="contractors-hero-secondary"
          >
            See the full technical breakdown
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
const ValueProps = () => {
  const items = [
    {
      icon: <Gauge className="w-6 h-6" />,
      title: 'Per-zone soil-moisture overrides',
      body: 'Ecowitt WH51/WH52 probes report every 15 min. TurfPulse decides per zone whether the controller fires — no flat global skip. Your MP Rotator efficiency stays intact; we just stop firing zones that are already at field capacity.',
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'Water reporting for commercial / HOA',
      body: 'Auto-generated weekly water-usage CSV per property with skip events logged, deltas before/after storms, and dollar-spot pressure flags. Drop into your client report email; built for properties that have to prove the savings.',
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: 'No new hardware to sell',
      body: "Your existing Rachio / Hydrawise / Rain Bird IQ / Toro Sentinel install stays exactly where it is. TurfPulse runs in the cloud, talks via each controller's API, and overlays soil + weather signals on top. Setup per property: ~5 min.",
    },
  ];
  return (
    <section
      className="border-b border-border py-14 sm:py-18 bg-white dark:bg-zinc-950"
      data-testid="contractors-value-props"
    >
      <div className="max-w-5xl mx-auto px-5">
        <h2 className="text-sm uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 mb-2">
          What it actually does for you
        </h2>
        <p className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 max-w-2xl leading-tight">
          Additive layer. Not another controller for your truck.
        </p>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <div
              key={i}
              className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/60 p-5"
              data-testid={`contractors-value-${i}`}
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                {it.icon}
              </div>
              <h3 className="mt-3 text-base font-bold text-zinc-900 dark:text-zinc-50">
                {it.title}
              </h3>
              <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                {it.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
const Honesty = () => (
  <section
    className="border-b border-border py-14 sm:py-18 bg-zinc-50 dark:bg-zinc-900/30"
    data-testid="contractors-honesty"
  >
    <div className="max-w-5xl mx-auto px-5">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
        What we <span className="text-emerald-700 dark:text-emerald-400">do</span> and <span className="text-amber-700 dark:text-amber-400">don't</span> do
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        We've gotten burned by vague vendor claims. Here's the unfiltered scope so you don't have to ask.
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-200">We do</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-zinc-800 dark:text-zinc-200">
            <li>• Per-zone soil-aware skip/fire decisions via controller API</li>
            <li>• Weekly water-usage + skip-event reports per property</li>
            <li>• Smith-Kerns dollar-spot pressure flags by region</li>
            <li>• TAMU 12% drought-floor override (no skip in real drought)</li>
            <li>• Auto-pause after applications you log</li>
            <li>• Open API so you can pull data into your own CRM</li>
          </ul>
        </div>
        <div className="rounded-xl border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 p-5">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h3 className="text-base font-bold text-amber-900 dark:text-amber-200">We don't</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-zinc-800 dark:text-zinc-200">
            <li>• Sell anything to your end clients (white-label only)</li>
            <li>• Replace your controller — your gear stays</li>
            <li>• Touch zone distribution / DU — that's still your job</li>
            <li>• Show your customers another bill or branded app</li>
            <li>• Compete with you on the install side</li>
            <li>• Pretend the math is magic — sources cited at /credits</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
const PilotForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    region: '',
    property_count: '6-20',
    property_mix: 'commercial',
    current_stack: [],
    biggest_pain: '',
    why_interested: '',
    referral_source: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleStack = (s) => {
    setForm((f) => ({
      ...f,
      current_stack: f.current_stack.includes(s)
        ? f.current_stack.filter((x) => x !== s)
        : [...f.current_stack, s],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/contractor-pilot`, form);
      if (res.data?.ok) {
        setSubmitted(true);
        toast.success(res.data.message || 'Got it — talk soon.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed — try emailing justin@getturfpulse.com directly.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="contractor-pilot-form"
        className="py-14 sm:py-18 bg-emerald-50/40 dark:bg-emerald-950/20 border-b border-border"
        data-testid="contractors-form-success"
      >
        <div className="max-w-3xl mx-auto px-5 text-center">
          <CheckCircle2 className="mx-auto w-12 h-12 text-emerald-600 dark:text-emerald-400" />
          <h2 className="mt-4 text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Got it — talk soon.
          </h2>
          <p className="mt-3 text-base text-zinc-700 dark:text-zinc-300">
            Justin reads every one of these personally and will reach out within 48h.
            If it's been longer, email <a href="mailto:justin@getturfpulse.com" className="font-bold text-emerald-700 dark:text-emerald-400 underline">justin@getturfpulse.com</a> directly — pilot applications never get auto-routed to a help queue.
          </p>
          <Link to="/ecowitt-to-rachio">
            <Button variant="outline" className="mt-6">
              In the meantime — read the technical breakdown
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contractor-pilot-form"
      className="py-14 sm:py-18 bg-white dark:bg-zinc-950 border-b border-border"
      data-testid="contractors-form-section"
    >
      <div className="max-w-3xl mx-auto px-5">
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-700 dark:text-amber-400">
          <Briefcase className="w-3.5 h-3.5" /> First-season pilot
        </div>
        <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
          Pilot one property. First season's on us.
        </h2>
        <p className="mt-3 text-sm sm:text-base text-zinc-700 dark:text-zinc-300 max-w-2xl leading-relaxed">
          We're picking <strong>5–10 irrigation pros</strong> for a comp'd first-season
          pilot on one of their commercial or HOA properties. In exchange we want
          honest weekly feedback and permission to use anonymized data in a
          public case study. No contract lock-in — keep using TurfPulse after the
          pilot at the contractor rate, or walk away clean.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 space-y-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/40 dark:bg-zinc-900/40 p-6"
          data-testid="contractors-form"
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-name" className="text-xs">Your name *</Label>
              <Input id="c-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contractor-name" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-xs">Email *</Label>
              <Input id="c-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contractor-email" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-company" className="text-xs">Company *</Label>
              <Input id="c-company" required value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="contractor-company" />
            </div>
            <div>
              <Label htmlFor="c-phone" className="text-xs">Phone (optional)</Label>
              <Input id="c-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contractor-phone" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label htmlFor="c-region" className="text-xs">Region served *</Label>
              <Input id="c-region" required placeholder="Round Rock, TX"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                data-testid="contractor-region" />
            </div>
            <div>
              <Label className="text-xs">Property count *</Label>
              <Select value={form.property_count}
                onValueChange={(v) => setForm({ ...form, property_count: v })}>
                <SelectTrigger data-testid="contractor-property-count"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-5">1–5</SelectItem>
                  <SelectItem value="6-20">6–20</SelectItem>
                  <SelectItem value="21-50">21–50</SelectItem>
                  <SelectItem value="50+">50+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mix *</Label>
              <Select value={form.property_mix}
                onValueChange={(v) => setForm({ ...form, property_mix: v })}>
                <SelectTrigger data-testid="contractor-property-mix"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Current controller stack (tap all that apply)</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {STACK_OPTIONS.map((s) => {
                const on = form.current_stack.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleStack(s)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors
                      ${on
                        ? 'bg-amber-500 border-amber-500 text-zinc-950'
                        : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:border-amber-400'
                      }`}
                    data-testid={`contractor-stack-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="c-pain" className="text-xs">Biggest pain point this season *</Label>
            <Textarea id="c-pain" required rows={3}
              placeholder="HOA water reports, drought ordinance, fungus complaints, client overwatering, route efficiency, controller failures..."
              value={form.biggest_pain}
              onChange={(e) => setForm({ ...form, biggest_pain: e.target.value })}
              data-testid="contractor-pain" />
          </div>

          <div>
            <Label htmlFor="c-why" className="text-xs">Why pilot TurfPulse specifically? *</Label>
            <Textarea id="c-why" required rows={3}
              placeholder="What does adding a soil-aware layer get you that your current stack can't?"
              value={form.why_interested}
              onChange={(e) => setForm({ ...form, why_interested: e.target.value })}
              data-testid="contractor-why" />
          </div>

          <div>
            <Label htmlFor="c-ref" className="text-xs">How did you hear about us? (optional)</Label>
            <Input id="c-ref" value={form.referral_source}
              placeholder="FB lawn group, Rachio Community thread, contractor referral..."
              onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
              data-testid="contractor-referral" />
          </div>

          <Button
            type="submit"
            disabled={busy}
            size="lg"
            className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold"
            data-testid="contractor-submit"
          >
            {busy ? 'Sending…' : 'Apply for the pilot'}
            <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            Justin reads every one of these personally. No marketing sequence,
            no SDR queue. Two-day max response time.
          </p>
        </form>
      </div>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
const Faq = () => {
  const items = [
    {
      q: "Will TurfPulse work with my existing Rachio + Hydrawise mixed fleet?",
      a: "Yes. We talk to each controller's API independently — you can have a fleet of mixed Rachio Gen 1/Gen 3 + Hydrawise + Rain Bird IQ and we run the soil-aware loop on top of all of them simultaneously. Each property is configured once.",
    },
    {
      q: "How is this different from Hydrawise's built-in flow meter?",
      a: "Flow meters tell you how much water flowed. Hydrawise's Predictive Watering adjusts schedule duration based on forecast. Neither knows what's actually happening at root zone. TurfPulse closes that gap by reading wireless soil-moisture probes (Ecowitt WH51/WH52) per zone and deciding fire/skip from real soil data instead of forecast inference.",
    },
    {
      q: "How much does it cost after the pilot?",
      a: "Contractor pricing is $9/month per property managed (annual, prepaid). No setup fees, no API integration fees. White-label option (your branding on weekly client reports) is +$15/month per property. Volume discounts at 25+ properties.",
    },
    {
      q: "Can I export the data into my own CRM / billing system?",
      a: "Yes. Every property has a JSON+CSV API token. Weekly summaries can also auto-email to any address. Open API for the things we don't expose by default.",
    },
    {
      q: "Do you brand anything to my clients?",
      a: "Nothing by default. The standard setup is fully white-label — your clients see your reports with your logo, not ours. We will never sell or upsell anything to your end clients directly.",
    },
    {
      q: "What if the pilot doesn't work for me?",
      a: "Walk away clean. No contract. We'd ask for a 15-minute exit call so we learn why, but no commercial commitment.",
    },
  ];

  return (
    <section
      className="py-14 sm:py-18 bg-zinc-50/60 dark:bg-zinc-900/30"
      data-testid="contractors-faq"
    >
      <div className="max-w-3xl mx-auto px-5">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 mb-6">
          Questions pros actually ask
        </h2>
        <div className="space-y-4">
          {items.map((it, i) => (
            <details
              key={i}
              className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
              data-testid={`contractors-faq-${i}`}
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold text-zinc-900 dark:text-zinc-50 text-sm sm:text-base">
                <span>{it.q}</span>
                <span className="text-amber-600 dark:text-amber-400 group-open:rotate-45 transition-transform shrink-0">+</span>
              </summary>
              <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                {it.a}
              </p>
            </details>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 p-5 flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
              Got a question not on this list?
            </p>
            <p className="text-sm text-zinc-800 dark:text-zinc-200">
              DM me on Facebook or email <a href="mailto:justin@getturfpulse.com" className="font-bold underline">justin@getturfpulse.com</a>. Real pros get real answers, not a chatbot.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
